import React, { ReactElement, useRef } from 'react'
import styled from '@emotion/styled'

import { Spinner } from '@mindfulchefuk/components/Loader'
import { Flex, Box } from '@mindfulchefuk/design-system/layout'
import { Text } from '@mindfulchefuk/design-system'

import { isCorsError } from './utils/isCorsError'
import { trackIFrameCorsError, trackNonCorsIFrameError } from './utils/tracking'
import { SecurePaymentState, SecurePaymentStage } from './SecurePayment.types'

export const PaymentIframe3DS = ({
  verifyPaymentSessionID,
  paymentState,
}: {
  verifyPaymentSessionID: (href: string) => void
  paymentState: SecurePaymentState
}): ReactElement => {
  const iFrameRef = useRef<HTMLIFrameElement>()

  const handleLoad = () => {
    try {
      const href = iFrameRef?.current?.contentWindow?.location?.href
      if (href) {
        verifyPaymentSessionID(href)
      }
    } catch (error) {
      if (isCorsError(error)) {
        trackIFrameCorsError()
        return
      }
      trackNonCorsIFrameError(error)
      throw error
    }
  }

  const DotAnimation = styled(Text)`
    &::after {
      animation: dots 2s linear infinite;
      content: '';
    }
    @keyframes dots {
      0%,
      20% {
        content: '.';
      }
      40% {
        content: '..';
      }
      60% {
        content: '...';
      }
      90%,
      100% {
        content: '';
      }
    }
  `

  switch (paymentState.stage) {
    case SecurePaymentStage.THREE_D_SECURE:
      return (
        <Box
          w="100%"
          height="440px"
          backgroundColor="white"
          position="relative"
          padding={12}
        >
          <DotAnimation>Loading</DotAnimation>
          <Box top={0} left={0} position="absolute" width="100%">
            <iframe
              title="Secure Payment"
              ref={iFrameRef}
              src={paymentState.url}
              width="100%"
              style={{
                border: 'none',
                minHeight: '440px',
              }}
              onLoad={handleLoad}
              data-testid="secure-payment"
              id="secure-payment"
            />
          </Box>
        </Box>
      )
    case SecurePaymentStage.VERIFY_PAYMENT:
    case SecurePaymentStage.MAKING_PAYMENT:
      return (
        <Flex
          w="100%"
          height="440px"
          backgroundColor="white"
          align="center"
          justify="center"
        >
          <Spinner />
        </Flex>
      )
    case SecurePaymentStage.COMPLETE:
    default:
      return null
  }
}
